import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
    Camera,
    Video as VideoIcon,
    RotateCcw,
    Check,
    X,
    AlertCircle,
    Loader2,
    MapPinOff,
    Circle,
    Square,
} from 'lucide-react';
import { useGeolocation, useCamera } from '@/lib/contexts/device-permissions-context';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { useTranslation } from 'react-i18next';

/**
 * Durée maximale d'enregistrement vidéo (en secondes) - auto-stop au-delà.
 */
const MAX_VIDEO_DURATION_SECONDS = 60;

/**
 * Sélectionne le meilleur mimeType vidéo supporté par MediaRecorder.
 */
function pickVideoMimeType() {
    if (typeof MediaRecorder === 'undefined') return null;
    const candidates = [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
    ];
    return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) ?? '';
}

function extensionForMime(mime) {
    if (!mime) return 'webm';
    if (mime.includes('mp4')) return 'mp4';
    if (mime.includes('webm')) return 'webm';
    return 'webm';
}

/**
 * Composant Sheet pour capturer une photo OU une vidéo horodatée via la caméra.
 *
 * Payload renvoyé à `onCapture` :
 *  - Photo : { mediaType: 'photo', mimeType, base64, fileName, dataUrl }
 *  - Vidéo : { mediaType: 'video', mimeType, blob, fileName, dataUrl, durationMs }
 */
export default function SheetPhotoCapture({ open, onClose, onCapture, isUploading = false }) {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const previewVideoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const capturedBlobRef = useRef(null);

    const {
        isEnabled: isGeolocationEnabled,
        toggleGeolocation,
        permissionStatus: geoPermissionStatus,
    } = useGeolocation();

    const { getStream: getCameraStream, isSupported: isCameraSupported } = useCamera();

    const [mode, setMode] = useState('photo'); // 'photo' | 'video'
    const [cameraStatus, setCameraStatus] = useState('pending'); // 'pending' | 'granted' | 'denied' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [capturedImage, setCapturedImage] = useState(null); // dataURL JPEG
    const [capturedVideoUrl, setCapturedVideoUrl] = useState(null); // objectURL d'un Blob
    const [capturedVideoMime, setCapturedVideoMime] = useState(null);
    const [capturedVideoDuration, setCapturedVideoDuration] = useState(0); // ms
    const [facingMode, setFacingMode] = useState('environment'); // 'environment' (arrière) ou 'user' (avant)
    const [isStartingCamera, setIsStartingCamera] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingElapsed, setRecordingElapsed] = useState(0); // secondes

    // Vérifier si la géolocalisation est active
    const isGeolocationActive = isGeolocationEnabled && geoPermissionStatus === 'granted';
    const hasCaptured = mode === 'photo' ? !!capturedImage : !!capturedVideoUrl;

    const videoMimeType = useMemo(() => pickVideoMimeType(), []);
    const isVideoSupported = typeof MediaRecorder !== 'undefined' && videoMimeType !== null;

    /**
     * Démarre le flux vidéo de la caméra via le hook useCamera.
     * En mode 'video', l'audio est également capturé.
     */
    const startCamera = useCallback(async () => {
        if (!isGeolocationActive) return;

        if (!isCameraSupported) {
            setCameraStatus('error');
            setErrorMessage("La caméra n'est pas disponible sur cet appareil.");
            return;
        }

        setIsStartingCamera(true);
        setErrorMessage('');

        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }

            const constraints = {
                video: {
                    facingMode,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: mode === 'video',
            };

            const stream = await getCameraStream(constraints);

            if (!stream) {
                setCameraStatus('denied');
                setErrorMessage(
                    "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.",
                );
                return;
            }

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }

            setCameraStatus('granted');
        } catch (err) {
            console.error('[Camera] Erreur accès caméra:', err);

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setCameraStatus('denied');
                setErrorMessage(
                    "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.",
                );
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setCameraStatus('error');
                setErrorMessage('Aucune caméra détectée sur cet appareil.');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                setCameraStatus('error');
                setErrorMessage('La caméra est utilisée par une autre application.');
            } else {
                setCameraStatus('error');
                setErrorMessage("Impossible d'accéder à la caméra. Vérifiez vos paramètres.");
            }
        } finally {
            setIsStartingCamera(false);
        }
    }, [facingMode, isGeolocationActive, isCameraSupported, getCameraStream, mode]);

    const clearRecordingTimer = useCallback(() => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
    }, []);

    const stopRecorder = useCallback(() => {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state !== 'inactive') {
            try {
                rec.stop();
            } catch (e) {
                console.warn('[Camera] stop recorder failed', e);
            }
        }
        clearRecordingTimer();
    }, [clearRecordingTimer]);

    /**
     * Arrête le flux vidéo (et l'enregistrement si en cours).
     */
    const stopCamera = useCallback(() => {
        stopRecorder();
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, [stopRecorder]);

    /**
     * Capture une photo depuis le flux vidéo.
     */
    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setCapturedImage(imageDataUrl);

        stopCamera();
    }, [stopCamera]);

    /**
     * Démarre l'enregistrement vidéo via MediaRecorder.
     */
    const startRecording = useCallback(() => {
        if (!streamRef.current || isRecording) return;
        if (!isVideoSupported) {
            setErrorMessage("L'enregistrement vidéo n'est pas supporté par ce navigateur.");
            setCameraStatus('error');
            return;
        }

        try {
            recordedChunksRef.current = [];
            const options = videoMimeType ? { mimeType: videoMimeType } : undefined;
            const recorder = new MediaRecorder(streamRef.current, options);
            mediaRecorderRef.current = recorder;

            const startedAt = Date.now();

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(recordedChunksRef.current, {
                    type: recorder.mimeType || videoMimeType || 'video/webm',
                });
                const url = URL.createObjectURL(blob);
                capturedBlobRef.current = blob;
                setCapturedVideoUrl(url);
                setCapturedVideoMime(blob.type);
                setCapturedVideoDuration(Date.now() - startedAt);
                setIsRecording(false);
                clearRecordingTimer();

                // Libérer la caméra une fois la vidéo enregistrée
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                }
            };

            recorder.start(250); // chunks toutes les 250ms
            setIsRecording(true);
            setRecordingElapsed(0);

            recordingTimerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startedAt) / 1000);
                setRecordingElapsed(elapsed);
                if (elapsed >= MAX_VIDEO_DURATION_SECONDS) {
                    stopRecorder();
                }
            }, 250);
        } catch (err) {
            console.error('[Camera] Erreur démarrage enregistrement:', err);
            setErrorMessage("Impossible de démarrer l'enregistrement vidéo.");
            setCameraStatus('error');
            setIsRecording(false);
        }
    }, [isRecording, videoMimeType, isVideoSupported, clearRecordingTimer, stopRecorder]);

    const stopRecording = useCallback(() => {
        stopRecorder();
    }, [stopRecorder]);

    /**
     * Réinitialise l'état de capture (photo ou vidéo).
     */
    const resetCapture = useCallback(() => {
        if (capturedVideoUrl) URL.revokeObjectURL(capturedVideoUrl);
        capturedBlobRef.current = null;
        setCapturedImage(null);
        setCapturedVideoUrl(null);
        setCapturedVideoMime(null);
        setCapturedVideoDuration(0);
        setRecordingElapsed(0);
    }, [capturedVideoUrl]);

    /**
     * Recommencer la capture (photo ou vidéo).
     */
    const retake = useCallback(() => {
        resetCapture();
        startCamera();
    }, [resetCapture, startCamera]);

    /**
     * Basculer entre caméra avant/arrière.
     */
    const toggleCamera = useCallback(() => {
        setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    }, []);

    /**
     * Bascule entre mode Photo et Vidéo (uniquement avant capture / enregistrement).
     */
    const handleModeChange = useCallback(
        (nextMode) => {
            if (nextMode === mode || isRecording || hasCaptured) return;
            setMode(nextMode);
        },
        [mode, isRecording, hasCaptured],
    );

    /**
     * Confirmer et envoyer la capture (photo ou vidéo).
     */
    const confirmCapture = useCallback(async () => {
        if (mode === 'photo') {
            if (!capturedImage) return;
            const base64Data = capturedImage.split(',')[1];
            const fileName = `photo_${Date.now()}.jpg`;
            onCapture({
                mediaType: 'photo',
                mimeType: 'image/jpeg',
                base64: base64Data,
                fileName,
                dataUrl: capturedImage,
            });
            return;
        }

        const blob = capturedBlobRef.current;
        if (!blob) return;
        const ext = extensionForMime(capturedVideoMime);
        const fileName = `video_${Date.now()}.${ext}`;
        console.log('[SheetPhotoCapture] onCapture vidéo', {
            blob,
            fileName,
            mimeType: capturedVideoMime || `video/${ext}`,
            durationMs: capturedVideoDuration,
            dataUrl: capturedVideoUrl,
        });
        onCapture({
            mediaType: 'video',
            mimeType: capturedVideoMime || `video/${ext}`,
            blob,
            fileName,
            dataUrl: capturedVideoUrl,
            durationMs: capturedVideoDuration,
        });
    }, [mode, capturedImage, capturedVideoUrl, capturedVideoMime, capturedVideoDuration, onCapture]);

    /**
     * Fermer le sheet et nettoyer.
     */
    const handleClose = useCallback(() => {
        if (isUploading || isRecording) return;

        stopCamera();
        resetCapture();
        setCameraStatus('pending');
        setErrorMessage('');
        onClose();
    }, [stopCamera, resetCapture, onClose, isUploading, isRecording]);

    // Démarrer la caméra à l'ouverture (et nettoyer à la fermeture)
    useEffect(() => {
        if (open && !hasCaptured && isGeolocationActive) {
            const timer = setTimeout(() => startCamera(), 100);
            return () => clearTimeout(timer);
        }

        if (!open) {
            stopCamera();
            resetCapture();
            setCameraStatus('pending');
            setErrorMessage('');
            setMode('photo');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hasCaptured, isGeolocationActive]);

    // Redémarrer la caméra quand on change facing mode ou mode (photo/vidéo)
    useEffect(() => {
        if (open && cameraStatus === 'granted' && !hasCaptured && isGeolocationActive && !isRecording) {
            startCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facingMode, mode]);

    // Cleanup à l'unmount
    useEffect(() => {
        return () => {
            if (capturedVideoUrl) URL.revokeObjectURL(capturedVideoUrl);
            clearRecordingTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formattedElapsed = useMemo(() => {
        const m = Math.floor(recordingElapsed / 60)
            .toString()
            .padStart(2, '0');
        const s = (recordingElapsed % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    }, [recordingElapsed]);

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && !isUploading && !isRecording && handleClose()}>
            <SheetContent className="w-full! max-w-full! md:w-[600px]! md:max-w-[600px]! flex flex-col p-0 h-full">
                <SheetHeader className="px-6 py-4 border-b border-border shrink-0">
                    <SheetTitle className="text-lg font-semibold text-foreground">
                        {mode === 'photo' ? t('folders:prendreUnePhotoHorodatee') : t('folders:enregistrerUneVideoHorodatee')}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 flex flex-col items-center justify-center bg-foreground-dark relative overflow-hidden">
                    {/* Toggle Photo / Vidéo */}
                    {isGeolocationActive && !hasCaptured && !isRecording && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center bg-foreground-dark/60 backdrop-blur-sm rounded-full p-1">
                            <button
                                type="button"
                                onClick={() => handleModeChange('photo')}
                                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm transition-colors ${
                                    mode === 'photo'
                                        ? 'bg-background text-foreground'
                                        : 'text-background hover:bg-background/10'
                                }`}
                            >
                                <Camera className="w-4 h-4" />
                                {t('folders:photo')}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleModeChange('video')}
                                disabled={!isVideoSupported}
                                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm transition-colors ${
                                    mode === 'video'
                                        ? 'bg-background text-foreground'
                                        : 'text-background hover:bg-background/10'
                                } ${!isVideoSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={!isVideoSupported ? t('folders:enregistrementVideoNonSupporte') : undefined}
                            >
                                <VideoIcon className="w-4 h-4" />
                                {t('folders:video')}
                            </button>
                        </div>
                    )}

                    {/* Indicateur d'enregistrement */}
                    {isRecording && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-destructive/90 text-background px-3 py-1.5 rounded-full text-sm font-medium">
                            <span className="w-2 h-2 rounded-full bg-background animate-pulse" />
                            REC {formattedElapsed} / {MAX_VIDEO_DURATION_SECONDS.toString().padStart(2, '0')}s
                        </div>
                    )}

                    {/* Géolocalisation non activée */}
                    {!isGeolocationActive && (
                        <div className="flex flex-col items-center justify-center gap-4 text-background p-6 text-center">
                            <div className="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center">
                                <MapPinOff className="w-10 h-10 text-warning" />
                            </div>
                            <p className="text-base font-medium">{t('folders:geolocalisationRequise')}</p>
                            <p className="text-sm text-background/70 max-w-xs">
                                {t('folders:pour')} {mode === 'photo' ? t('folders:prendreUnePhoto') : t('folders:enregistrerUneVideo')} {t('folders:horodateeVousDevezActiverLaGeolocalisation')}
                            </p>
                            <Button
                                variant="soft"
                                color="secondary"
                                onClick={toggleGeolocation}
                                className="mt-2 text-primary-foreground"
                            >
                                <MapPinOff className="w-4 h-4" />
                                {t('folders:activerLaGeolocalisation')}
                            </Button>
                        </div>
                    )}

                    {/* État de chargement initial */}
                    {isGeolocationActive && (cameraStatus === 'pending' || isStartingCamera) && !hasCaptured && (
                        <div className="flex flex-col items-center justify-center gap-4 text-background">
                            <Loader2 className="w-12 h-12 animate-spin" />
                            <p className="text-sm">{t('folders:accesALaCamera')}</p>
                        </div>
                    )}

                    {/* État d'erreur ou refus */}
                    {isGeolocationActive &&
                        (cameraStatus === 'denied' || cameraStatus === 'error') &&
                        !isStartingCamera &&
                        !hasCaptured && (
                            <div className="flex flex-col items-center justify-center gap-4 text-background p-6 text-center">
                                <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-10 h-10 text-destructive" />
                                </div>
                                <p className="text-sm max-w-xs">{errorMessage}</p>
                                <Button variant="soft" color="secondary" onClick={startCamera} className="mt-2">
                                    <RotateCcw className="w-4 h-4" />
                                    {t('folders:reessayer')}
                                </Button>
                            </div>
                        )}

                    {/* Flux vidéo en direct */}
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full h-full object-contain ${
                            isGeolocationActive && cameraStatus === 'granted' && !hasCaptured && !isStartingCamera
                                ? ''
                                : 'hidden'
                        }`}
                    />

                    {/* Bouton pour changer de caméra */}
                    {isGeolocationActive &&
                        cameraStatus === 'granted' &&
                        !hasCaptured &&
                        !isStartingCamera &&
                        !isRecording && (
                            <Button
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={toggleCamera}
                                className="absolute top-4 right-4 w-10 h-10 bg-foreground-dark/50 rounded-full flex items-center justify-center text-background hover:bg-foreground-dark/70 transition-colors"
                                title={t('folders:changerDeCamera')}
                            >
                                <RotateCcw className="w-5 h-5" />
                            </Button>
                        )}

                    {/* Photo capturée */}
                    {isGeolocationActive && mode === 'photo' && capturedImage && (
                        <img src={capturedImage} alt={t('folders:photoCapturee')} className="w-full h-full object-contain" />
                    )}

                    {/* Vidéo capturée - player avec contrôles */}
                    {isGeolocationActive && mode === 'video' && capturedVideoUrl && (
                        <video
                            ref={previewVideoRef}
                            src={capturedVideoUrl}
                            controls
                            playsInline
                            className="w-full h-full object-contain bg-foreground-dark"
                        />
                    )}

                    {/* Canvas caché pour la capture photo */}
                    <canvas ref={canvasRef} className="hidden" />
                </div>

                <SheetFooter className="px-6 py-4 border-t border-border shrink-0">
                    {!isGeolocationActive ? (
                        // Bouton fermer si géoloc non activée
                        <div className="flex items-center justify-center w-full">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                onClick={handleClose}
                                disabled={isUploading}
                            >
                                <X className="w-4 h-4" />
                                {t('folders:fermer')}
                            </Button>
                        </div>
                    ) : !hasCaptured ? (
                        // Boutons avant capture
                        <div className="flex items-center justify-center w-full gap-4">
                            <Button
                                type="button"
                                variant="soft"
                                color="neutral"
                                onClick={handleClose}
                                disabled={isUploading || isRecording}
                            >
                                <X className="w-4 h-4" />
                                {t('folders:annuler')}
                            </Button>

                            {mode === 'photo' ? (
                                <SubmitButton
                                    type="button"
                                    onClick={capturePhoto}
                                    disabled={cameraStatus !== 'granted' || isStartingCamera}
                                >
                                    <Camera className="w-5 h-5" />
                                    {t('folders:capturer')}
                                </SubmitButton>
                            ) : !isRecording ? (
                                <SubmitButton
                                    type="button"
                                    onClick={startRecording}
                                    disabled={cameraStatus !== 'granted' || isStartingCamera || !isVideoSupported}
                                >
                                    <Circle className="w-5 h-5 fill-current text-destructive" />
                                    {t('folders:demarrerLEnregistrement')}
                                </SubmitButton>
                            ) : (
                                <SubmitButton type="button" onClick={stopRecording}>
                                    <Square className="w-5 h-5 fill-current" />
                                    {t('folders:arreter')}
                                </SubmitButton>
                            )}
                        </div>
                    ) : (
                        // Boutons après capture
                        <div className="flex items-center justify-center w-full gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                onClick={retake}
                                disabled={isUploading}
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t('folders:reprendre')}
                            </Button>
                            <SubmitButton
                                type="button"
                                onClick={confirmCapture}
                                isSubmitting={isUploading}
                                loadingText={t('folders:envoi')}
                            >
                                <Check className="w-5 h-5" />
                                {t('folders:valider')}
                            </SubmitButton>
                        </div>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
